export const DEFAULT_OCTET_STREAM = "application/octet-stream";

/**
 * Returns the bare extension (without leading dot) from a file name or path,
 * or an empty string if none is found. Accepts null/undefined.
 * e.g. "statement.pdf" → "pdf"
 */
export const getExtension = (fileName?: string | null): string => {
  if (!fileName) {
    return "";
  }

  const extensionIndex = fileName.lastIndexOf(".");

  if (extensionIndex < 0 || extensionIndex === fileName.length - 1) {
    return "";
  }

  return fileName.slice(extensionIndex + 1).toLowerCase();
};

/** Maps bare file extension → MIME content type. */
const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  pdf: "application/pdf",
  zip: "application/zip",
  png: "image/png",
  jpeg: "image/jpeg",
  jpg: "image/jpeg", // listed after jpeg so .jpg overwrites .jpeg in the reverse map
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
};

/**
 * Maps MIME content type → file extension with leading dot, derived from
 * EXTENSION_TO_CONTENT_TYPE. Where multiple extensions share a type (e.g.
 * jpg/jpeg), "jpg" wins as the canonical download suffix for image/jpeg since
 * it is listed first. "application/x-zip-compressed" is added as an alias.
 */
const CONTENT_TYPE_TO_EXTENSION: Record<string, string> = {
  "application/x-zip-compressed": ".zip",
  ...Object.fromEntries(
    Object.entries(EXTENSION_TO_CONTENT_TYPE).map(([ext, mime]) => [
      mime,
      `.${ext}`,
    ]),
  ),
};

/**
 * Returns the file extension with a leading dot for use as a download suffix,
 * or undefined if the content type is unrecognised.
 * e.g. "application/pdf" → ".pdf"
 */
export const getExtensionFromContentType = (
  contentType: string,
): string | undefined => {
  const normalized = contentType.split(";")[0].trim().toLowerCase();
  return CONTENT_TYPE_TO_EXTENSION[normalized];
};

/**
 * Returns the MIME content type for a bare file extension, or an empty string
 * if unrecognised.
 * e.g. "pdf" → "application/pdf"
 */
export const getContentTypeFromExtension = (extension: string): string =>
  EXTENSION_TO_CONTENT_TYPE[extension.toLowerCase()] ?? "";
